import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import ShareIcon from "@mui/icons-material/Share";

import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";

import ConfirmationDialog from "lib/components/dialog/ConfirmationDialog";

import DatePicker from "utils/elements/DatePicker";
import Filter from "utils/Filter";
import { asTitle, flipElement } from "utils/Utils";

import ExtractAccessDialog from "./ExtractAccessDialog";
import {
	deleteExtract,
	ExtractInfo,
	ExtractStatus,
	getExtractContent,
	getExtracts,
	updateExtractRights
} from "./ExtractService";
import ShareExtractDialog from "./ShareExtractDialog";
import { AssistantTwoTone } from "@mui/icons-material";

// TODO: this is just a temporary page until more blueprints are available.
const ExtractOverview: React.FC = () => {
	const name = "extract";
	const { t } = useTranslation();

	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [selected, setSelected] = useState<number[]>([]);
	const [extracts, setExtracts] = useState<ExtractInfo[]>([]);
	const [filter, setFilter] = useState("");
	const [showArchived, setShowArchived] = useState(false);
	const [userAccessId, setUserAccessId] = useState<number>();
	const [createdAfter, setCreatedAfter] = useState<Date | null>(null);

	const [shareExtractDialog, setShareExtractDialog] = useState(false);
	const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);

	useEffect(() => loadExtracts(), []);

	function loadExtracts(): void {
		getExtracts()
			.then(setExtracts)
			.catch(() => {
				setError(true);
				setErrorMessage(
					t("loadDocumentError", { document: "extract_other" })
				);
			});
	}

	function selectAll(event: React.ChangeEvent<HTMLInputElement>): void {
		if (event.target.checked) {
			setSelected(filterExtracts().map(extract => extract.id));
		} else {
			setSelected([]);
		}
	}

	function select(id: number): void {
		setSelected(flipElement(id, selected));
	}

	function filterExtracts(): ExtractInfo[] {
		return extracts
			.filter(
				extract =>
					showArchived || extract.status === ExtractStatus.ACTIVE
			)
			.filter(
				extract =>
					createdAfter === null ||
					parseISO(extract.created) > createdAfter
			)
			.filter(extract => {
				if (filter === "") {
					return true;
				}

				const lowerCaseFilter = filter.toLowerCase();

				return (
					extract.name.toLowerCase().indexOf(lowerCaseFilter) !==
						-1 ||
					extract.trajectoryName
						.toLowerCase()
						.indexOf(lowerCaseFilter) !== -1
				);
			});
	}

	async function deleteDocuments(): Promise<void> {
		for (const extract of selected) {
			await deleteExtract(extract);
		}

		setSelected([]);
		setConfirmDeleteDialog(false);
		loadExtracts();
	}

	async function shareExtract(users: string[]): Promise<void> {
		setShareExtractDialog(false);
		for (const extract of selected) {
			await updateExtractRights(extract, users);
		}
		setSelected([]);
	}

	function downloadExtract(id: number, name: string): void {
		getExtractContent(id).then(data => {
			const url = window.URL.createObjectURL(data);
			const a = document.createElement("a");
			a.href = url;
			a.download = name + ".pdf";
			a.click();
		});
	}

	return (
		<>
			{error && (
				<Alert id="error-message" severity="error">
					{errorMessage}
				</Alert>
			)}

			<Grid container alignItems="center">
				<Grid item xs={4}>
					<Grid container alignItems="center" spacing={2}>
						<Grid item>
							<FormControlLabel
								control={
									<Switch
										size="small"
										checked={showArchived}
										onChange={() =>
											setShowArchived(!showArchived)
										}
									/>
								}
								label={t("archived")}
							/>
						</Grid>
						<Grid item>
							<Tooltip title={asTitle(t("delete"))}>
								<span>
									<IconButton
										id="delete-button"
										aria-label="delete"
										onClick={(): void =>
											setConfirmDeleteDialog(true)
										}
										disabled={selected.length === 0}
										size="large">
										<DeleteIcon />
									</IconButton>
								</span>
							</Tooltip>
						</Grid>
						<Grid item>
							<Tooltip title={asTitle(t("share"))}>
								<span>
									<IconButton
										id="share-button"
										aria-label="share"
										onClick={(): void =>
											setShareExtractDialog(true)
										}
										disabled={selected.length === 0}
										size="large">
										<ShareIcon />
									</IconButton>
								</span>
							</Tooltip>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={4}>
					<Typography id="title" variant="h5" align="center">
						{asTitle(t(name + "_other"))}
					</Typography>
				</Grid>
				<Grid item xs={4}>
					<Filter onChange={setFilter} />
				</Grid>
			</Grid>
			<Grid>
				<DatePicker
					label={asTitle(t("createdAfter"))}
					disableFuture
					inputProps={{ variant: "standard" }}
					value={createdAfter}
					onChange={(date: Date | null): void => {
						if (date !== null) {
							date.setHours(0);
							date.setMinutes(0);
							date.setSeconds(0);
							date.setMilliseconds(0);
						}
						setCreatedAfter(date);
					}}
				/>
			</Grid>

			<Table size="small" aria-label="documents">
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox">
							<Checkbox
								id="select-all"
								indeterminate={
									selected.length > 0 &&
									selected.length < extracts.length
								}
								checked={
									selected.length > 0 &&
									selected.length === extracts.length
								}
								onChange={selectAll}
							/>
						</TableCell>
						<TableCell>{asTitle(t("title"))}</TableCell>
						<TableCell>{asTitle(t("trajectory"))}</TableCell>
						<TableCell>{asTitle(t("created"))}</TableCell>
						<TableCell />
					</TableRow>
				</TableHead>
				<TableBody>
					{filterExtracts().map((row, index) => (
						<TableRow
							id={"row-" + row.id}
							key={index + "-" + row.name}
							hover>
							<TableCell padding="checkbox">
								<Checkbox
									id={"select-" + row.id}
									checked={selected.includes(row.id)}
									onChange={(): void => select(row.id)}
								/>
							</TableCell>
							<TableCell
								id={"name-" + row.id}
								component="th"
								scope="row">
								<Link
									to="#"
									onClick={() =>
										downloadExtract(
											row.id,
											row.name + "-" + row.trajectoryName
										)
									}>
									{row.name}
								</Link>
							</TableCell>
							<TableCell id={"trajectory-" + row.id}>
								<Link to={"/trajectory/" + row.trajectoryId}>
									{row.trajectoryName}
								</Link>
							</TableCell>
							<TableCell id={"created-" + row.id}>
								{format(new Date(row.created), "dd/MM/yyyy")}
							</TableCell>
							<TableCell
								id={"users-" + row.id}
								padding="checkbox">
								<IconButton
									size="small"
									onClick={() => setUserAccessId(row.id)}>
									<PeopleIcon fontSize="inherit" />
								</IconButton>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>

			{confirmDeleteDialog && (
				<ConfirmationDialog
					title={t("deleteDocumentTitle", {
						document: name,
						count: selected.length
					})}
					dialogText={t("deleteDocumentMessage", {
						document: name,
						count: selected.length
					})}
					cancelLabel={t("cancel")}
					confirmLabel={t("delete")}
					onClose={(): void => setConfirmDeleteDialog(false)}
					onConfirm={deleteDocuments}
				/>
			)}

			{shareExtractDialog && (
				<ShareExtractDialog
					onClose={() => setShareExtractDialog(false)}
					onConfirm={shareExtract}
				/>
			)}

			{userAccessId !== undefined && (
				<ExtractAccessDialog
					id={userAccessId}
					onClose={() => setUserAccessId(undefined)}
				/>
			)}
		</>
	);
};

export default ExtractOverview;
